import { QRCodeSVG } from 'qrcode.react';
import { renderToString } from 'react-dom/server';
import printJS from 'print-js';
import { formatMoney } from './formating';
import { lang } from './langs';
import { getCookie } from './http-client';

// Función para generar un SVG de código QR real
export function generateQrSvg(text, size = 128) {
  return renderToString(<QRCodeSVG value={text} size={size} level="H" />);
}

/**
 * Patrón Strategy para diferentes tipos de recibos
 */
const ReceiptStrategies = {
  /**
   * Estrategia para Facturación Electrónica
   */
  ELECTRONIC: {
    getExtraHeader: (data) => `
      <div><strong>Info:</strong> ${data?.data?.resolutionText || ''}</div>
    `,
    getBillLabel: () => 'Factura:',
    getBillNumber: (data) =>
      `${data?.data?.prefix || ''}${data?.data?.number || ''}`,
    showTaxes: () => true,
    getExtraFooter: (data) => {
      const qrCodeText = data?.data?.electronic_invoice?.QRCode || '';
      const urlRegex = /(https?:\/\/[^\s]+)/;
      const matchUrl = qrCodeText.match(urlRegex);
      const qrSvg = generateQrSvg(matchUrl ? matchUrl[0] : '');

      const cufeRegex = /CUFE:\s*([a-f0-9]+)/i;
      const matchCufe = qrCodeText.match(cufeRegex);
      const cufe = matchCufe && matchCufe.length > 0 ? matchCufe[0] : '';

      return `
        <div style="text-align: center; margin-top: 12px;">
            ${qrSvg}
            <div style="font-size: 10px; margin-top: 4px;">Verifica tu factura</div>
            <div style="font-size: 10px; word-wrap: break-word; word-break: break-all; max-width: 200px; margin: 0 auto;">
                CUFE: ${cufe}
            </div>
        </div>
        <div style="text-align: center; margin-top: 10px;">
          ${data?.data?.electronic_invoice?.techProviderDefaultFootNote || ''}
        </div>
      `;
    },
  },

  /**
   * Estrategia para recibos estándar (no electrónicos)
   */
  STANDARD: {
    getExtraHeader: () => '',
    getBillLabel: () => 'Nro. Venta:',
    getBillNumber: (data) => data?.bill_id || '',
    showTaxes: () => false,
    getExtraFooter: () => '',
  },
};

function extractEmissionDate(data) {
  let dateStr = "";
  const electronicInvoice = data?.data?.electronic_invoice || data?.data?.invoice?.electronic_invoice;

  if (electronicInvoice?.QRCode) {
    const qrCodeText = electronicInvoice.QRCode;
    const match = qrCodeText.match(/FecFac:\s*([^\n]+)/);
    if (match) {
      dateStr = match[1].trim();
    }
  }

  if (dateStr) {
    return dateStr;
  }

  if (data?.data?.date) {
    return data.data.date;
  }
  if (data?.created_at) {
    return new Date(data.created_at).toLocaleString();
  }

  return new Date().toLocaleString();
}

export function generateReceiptHtmlContent(data, profile = null) {
  // Selección de estrategia
  const strategyType = data?.data?.electronic_invoice
    ? 'ELECTRONIC'
    : 'STANDARD';
  const strategy = ReceiptStrategies[strategyType];

  // Priorizar el perfil pasado por parámetro, fallback a la cookie
  let user_info = profile;
  if (!user_info) {
    const rawCookieData = getCookie('data');
    user_info = rawCookieData ? JSON.parse(rawCookieData) : {};
  }

  // Construcción de filas comunes
  let methodsHtml = '';
  data?.methods?.forEach((method) => {
    methodsHtml += `<tr><td>${lang('es', method.method)}</td><td style="text-align: right;">${formatMoney(method.value)}</td></tr>`;
  });

  let productsHtml = '';
  data?.items?.forEach((item) => {
    const subtotal = item.value * item.amount;
    productsHtml += `<tr><td>${item.name} x${item.amount}</td><td style="text-align: right;">${formatMoney(subtotal)}</td></tr>`;
  });

  let taxesHtml = '';
  if (strategy.showTaxes()) {
    data?.taxes?.forEach((item) => {
      const percent = (item.value * 100).toFixed(0);
      taxesHtml += `<tr><td>${item.metadata.name} (${percent}%)</td><td style="text-align: right;">${formatMoney(Math.round(item.subtotal))}</td></tr>`;
    });
  }

  let warrantiesHtml = '';
  let hasWarranties = false;
  data?.items?.forEach((item) => {
    const warrantyValue = item.warranty || item.product?.warranty;
    if (warrantyValue && warrantyValue.trim() !== '') {
      hasWarranties = true;
      warrantiesHtml += `<tr><td style="padding: 2px 0;">${item.name}</td><td style="text-align: right; padding: 2px 0; font-weight: bold;">${warrantyValue}</td></tr>`;
    }
  });

  let warrantiesSection = '';
  if (hasWarranties) {
    warrantiesSection = `
      <div style="border-top: 1px dashed #000; margin: 6px 0;"></div>
      <div style="margin-top: 8px;">
        <strong>Garantías de Productos:</strong>
        <table style="width: 100%; margin-top: 4px; font-size: 10px;">
          ${warrantiesHtml}
        </table>
      </div>
    `;
  }

  return `
    <div id="print-content" style="
      width: 100%;
      min-width: 58mm;
      max-width: 80mm;
      font-family: 'Courier New', monospace;
      font-size: 11px;
      padding: 8px;
      color: #000;
      margin: 0 auto;
    ">
      <div style="text-align: center;">
        <strong>${user_info.companyName || ''}</strong><br/> 
        ${user_info.phone || ''}<br/>
        ${user_info.address || ''}<br/>
        -------------------------------
      </div>

      <div style="margin-top: 8px;">
        ${strategy.getExtraHeader(data)}
        <div><strong>${strategy.getBillLabel()}</strong> ${strategy.getBillNumber(data)}</div>
        <div><strong>Cliente:</strong> ${data?.tercero?.name || ''} (${data?.tercero?.identification || ''})</div>
        <div><strong>Fecha:</strong> ${extractEmissionDate(data)}</div>
      </div>

      <div style="margin-top: 10px;">
        <table style="width: 100%;">
          ${productsHtml}
        </table>
      </div>

      <div style="border-top: 1px dashed #000; margin: 6px 0;"></div>

      <table style="width: 100%;">
        ${taxesHtml}
        <tr><td><strong>Total</strong></td><td style="text-align: right;"><strong>${formatMoney(data.total || 0)}</strong></td></tr>
      </table>

      <div style="border-top: 1px dashed #000; margin: 6px 0;"></div>

      <table style="width: 100%;">
        <tr><td colspan="2"><strong>Métodos de pago:</strong></td></tr>
        ${methodsHtml}
      </table>

      ${warrantiesSection}

      ${strategy.getExtraFooter(data)}

      <div style="text-align: center; margin-top: 10px;">
        ¡Gracias por tu compra!
      </div>
    </div>
  `;
}

export function generateComprobanteHtml(data, profile = null) {
  let user_info = profile;
  if (!user_info) {
    const rawCookieData = getCookie('data');
    user_info = rawCookieData ? JSON.parse(rawCookieData) : {};
  }

  const formatCurrency = (value) => formatMoney(value);

  let linesHtml = '';
  data?.lineas?.forEach((line) => {
    linesHtml += `
      <tr>
        <td style="border-bottom: 0.5px solid #eee; padding: 4px 0;">${line.users_accounts?.account?.account}${line.users_accounts?.code?.toString().padStart(2, '0') || ''}</td>
        <td style="border-bottom: 0.5px solid #eee; padding: 4px 0;">${line.users_accounts?.description || ''}</td>
        <td style="border-bottom: 0.5px solid #eee; padding: 4px 0; text-align: right;">${formatCurrency(line.valor_debito)}</td>
        <td style="border-bottom: 0.5px solid #eee; padding: 4px 0; text-align: right;">${formatCurrency(line.valor_credito)}</td>
      </tr>
    `;
  });

  const debitoTotal =
    data.lineas?.reduce(
      (acc, curr) => acc + (Number(curr.valor_debito) || 0),
      0
    ) || 0;
  const creditoTotal =
    data.lineas?.reduce(
      (acc, curr) => acc + (Number(curr.valor_credito) || 0),
      0
    ) || 0;

  return `
    <div id="print-comprobante" style="
      width: 100%;
      padding: 20px;
      font-family: Arial, sans-serif;
      font-size: 12px;
      color: #333;
    ">
      <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 20px;">
        <div>
          <h1 style="margin: 0; font-size: 18px;">${user_info.companyName || 'EasyPos'}</h1>
          <p style="margin: 2px 0;">NIT: ${user_info.nit || ''}</p>
          <p style="margin: 2px 0;">${user_info.address || ''}</p>
          <p style="margin: 2px 0;">Tel: ${user_info.phone || ''}</p>
        </div>
        <div style="text-align: right;">
          <h2 style="margin: 0; color: #1a73e8;">COMPROBANTE CONTABLE</h2>
          <p style="margin: 2px 0; font-weight: bold;">Nro: ${data.id || ''}</p>
          <p style="margin: 2px 0;">Fecha: ${new Date(data.created_at || Date.now()).toLocaleDateString()}</p>
        </div>
      </div>

      <div style="background-color: #f8f9fa; border-radius: 4px; padding: 12px; margin-bottom: 20px;">
        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 10px;">
          <div><strong>Descripción:</strong> ${data.descripcion || 'Sin descripción'}</div>
          <div><strong>Tipo Doc:</strong> ${data.document_type || ''}</div>
          <div><strong>Documento Ref:</strong> ${data.document || ''} - ${data.document_number || ''}</div>
        </div>
      </div>

      <table style="width: 100%; border-collapse: collapse; margin-bottom: 20px;">
        <thead style="background-color: #eee;">
          <tr>
            <th style="text-align: left; padding: 8px;">Cuenta</th>
            <th style="text-align: left; padding: 8px;">Nombre Cuenta</th>
            <th style="text-align: right; padding: 8px;">Débito</th>
            <th style="text-align: right; padding: 8px;">Crédito</th>
          </tr>
        </thead>
        <tbody>
          ${linesHtml}
        </tbody>
        <tfoot>
          <tr style="font-weight: bold; background-color: #f8f9fa;">
            <td colspan="2" style="padding: 8px; text-align: right;">TOTALES:</td>
            <td style="padding: 8px; text-align: right;">${formatCurrency(debitoTotal)}</td>
            <td style="padding: 8px; text-align: right;">${formatCurrency(creditoTotal)}</td>
          </tr>
        </tfoot>
      </table>

      <div style="margin-top: 50px; display: flex; justify-content: space-around;">
        <div style="width: 200px; border-top: 1px solid #333; text-align: center; padding-top: 5px;">
          Preparado por
        </div>
        <div style="width: 200px; border-top: 1px solid #333; text-align: center; padding-top: 5px;">
          Autorizado por
        </div>
      </div>

      <div style="text-align: center; margin-top: 30px; font-size: 10px; color: #777;">
        Impreso el ${new Date().toLocaleString()} - Generado por EasyPos
      </div>
    </div>
  `;
}

export function printReceipt(data, profile = null) {
  const htmlContent = generateReceiptHtmlContent(data, profile);

  let container = document.getElementById('print-container');
  if (!container) {
    container = document.createElement('div');
    container.id = 'print-container';
    container.style.display = 'none';
    document.body.appendChild(container);
  }

  container.innerHTML = htmlContent;

  printJS({
    printable: 'print-content',
    type: 'html',
    targetStyles: ['*'],
  });
}

export function printComprobante(data, profile = null) {
  const htmlContent = generateComprobanteHtml(data, profile);

  let container = document.getElementById('print-container');
  if (!container) {
    container = document.createElement('div');
    container.id = 'print-container';
    container.style.display = 'none';
    document.body.appendChild(container);
  }

  container.innerHTML = htmlContent;

  printJS({
    printable: 'print-comprobante',
    type: 'html',
    targetStyles: ['*'],
  });
}

export function generateDailyClosureHtml(data, profile = null) {
  const { metadata, sections, totals } = data;
  const user_info = profile || {};
  const formatCurrency = (val) => formatMoney(val || 0);

  // Extraer datos de las secciones
  const comprobantes = sections?.find(s => s.name === 'comprobantes')?.data || [];
  const payments = sections?.find(s => s.name === 'totals_by_payment_method')?.data || [];
  const taxes = sections?.find(s => s.name === 'totals_by_tax_rate')?.data || [];

  let comprobantesHtml = '';
  comprobantes.forEach(c => {
    comprobantesHtml += `<div>• ${c.document_type} ${c.prefix || ''}: ${c.number_start} - ${c.number_end}</div>`;
  });

  let paymentsHtml = '';
  payments.forEach(p => {
    paymentsHtml += `<tr><td>${p.method}</td><td style="text-align: right;">${formatCurrency(p.value)}</td></tr>`;
  });

  let taxesHtml = '';
  taxes.forEach(t => {
    taxesHtml += `<tr><td>IVA ${t.tarifa}</td><td style="text-align: right;">${formatCurrency(t.iva)}</td></tr>`;
  });

  return `
    <div id="print-daily-closure" style="
      width: 100%;
      min-width: 58mm;
      max-width: 80mm;
      font-family: 'Courier New', monospace;
      font-size: 11px;
      padding: 8px;
      color: #000;
      margin: 0 auto;
    ">
      <div style="text-align: center;">
        <strong>${user_info.companyName || 'EasyPos'}</strong><br/>
        Arqueo / Cierre de Caja<br/>
        -------------------------------
      </div>

      <div style="margin-top: 8px;">
        <div><strong>Periodo:</strong> ${metadata?.period_from} a ${metadata?.period_to}</div>
        <div><strong>Generado:</strong> ${metadata?.generated_at}</div>
        ${metadata?.closure_log_id ? `<div><strong>Cierre ID:</strong> #${metadata.closure_log_id}</div>` : ''}
      </div>

      <div style="border-top: 1px dashed #000; margin: 6px 0;"></div>

      <table style="width: 100%;">
        <tr><td>Saldo Inicial Contable:</td><td style="text-align: right;">${formatCurrency(totals?.saldo_inicial)}</td></tr>
        ${totals?.saldo_inicial_manual !== undefined ? `<tr><td>Saldo Inicial Manual:</td><td style="text-align: right;">${formatCurrency(totals?.saldo_inicial_manual)}</td></tr>` : ''}
        <tr><td>(+) Ventas del Día:</td><td style="text-align: right;">${formatCurrency(totals?.total_ventas)}</td></tr>
        <tr><td>(-) Notas Crédito:</td><td style="text-align: right;">${formatCurrency(totals?.total_notas_credito)}</td></tr>
        <tr><td>(+) Recibos de Caja:</td><td style="text-align: right;">${formatCurrency(totals?.total_recibos)}</td></tr>
        <tr><td><strong>(=) Total Ingresos:</strong></td><td style="text-align: right;"><strong>${formatCurrency(totals?.total_ingresos)}</strong></td></tr>
        <tr><td>(-) Ventas No Efectivo:</td><td style="text-align: right;">${formatCurrency(totals?.total_no_efectivo)}</td></tr>
        <tr><td>(-) Egresos Efectivo:</td><td style="text-align: right;">${formatCurrency(totals?.egresos_efectivo)}</td></tr>
        <tr style="font-size: 12px; font-weight: bold; border-top: 1px dashed #000;">
          <td>(=) Saldo Final Esperado:</td>
          <td style="text-align: right;">${formatCurrency(totals?.saldo_final)}</td>
        </tr>
      </table>

      <div style="border-top: 1px dashed #000; margin: 6px 0;"></div>

      <div style="margin-top: 6px;">
        <strong>Rangos de Comprobantes:</strong>
        ${comprobantesHtml || '<div>Ninguno</div>'}
      </div>

      <div style="border-top: 1px dashed #000; margin: 6px 0;"></div>

      <table style="width: 100%;">
        <tr><td colspan="2"><strong>Consolidado Medios de Pago:</strong></td></tr>
        ${paymentsHtml}
      </table>

      <div style="border-top: 1px dashed #000; margin: 6px 0;"></div>

      <table style="width: 100%;">
        <tr><td colspan="2"><strong>Resumen de Impuestos:</strong></td></tr>
        ${taxesHtml || '<tr><td colspan="2">Sin impuestos recaudados</td></tr>'}
      </table>

      ${metadata?.observations ? `
        <div style="border-top: 1px dashed #000; margin: 6px 0;"></div>
        <div><strong>Observaciones:</strong><br/>${metadata.observations}</div>
      ` : ''}

      <div style="text-align: center; margin-top: 20px;">
        ¡Cierre de caja guardado con éxito!
      </div>
    </div>
  `;
}

export function printDailyClosure(data, profile = null) {
  const htmlContent = generateDailyClosureHtml(data, profile);

  let container = document.getElementById('print-container');
  if (!container) {
    container = document.createElement('div');
    container.id = 'print-container';
    container.style.display = 'none';
    document.body.appendChild(container);
  }

  container.innerHTML = htmlContent;

  printJS({
    printable: 'print-daily-closure',
    type: 'html',
    targetStyles: ['*'],
  });
}
